/**
 * schemas/config/approvalRules.ts
 * ──────────────────────────────────────────────────────────────────────────
 * The approval engine's rule set. A rule is WHEN (a condition) THEN (one or
 * two named approvers).
 *
 * `threshold_minor` and `dual_over_minor` on the policy now SEED rules on save
 * rather than being evaluated directly — so everything the engine acts on is
 * visible and editable in one list, instead of two numbers behaving as
 * invisible extra rules.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { z } from 'zod';
import { minorNullable } from '../common';

/** What a rule can trigger on. */
export const RULE_FIELDS = ['amount', 'category', 'holderType', 'instrument', 'noReceipt'] as const;
export type RuleField = (typeof RULE_FIELDS)[number];

/** The chip label for each trigger, in the WHEN column's own words. */
export const RULE_FIELD_LABEL: Record<RuleField, string> = {
  amount: 'Amount is at or above',
  category: 'Category is',
  holderType: 'Holder type is',
  instrument: 'Instrument is',
  noReceipt: 'No receipt is attached',
};

export const approvalRuleSchema = z.object({
  id: z.string(),
  on_field: z.string(),
  /** Set for `amount` rules; null for the rest. */
  value_minor: minorNullable,
  /** Set for category / holderType / instrument rules; null for the rest. */
  value_text: z.string().nullable(),
  /** One or two names, in the order they must approve. */
  approvers: z.array(z.string()),
  /**
   * The API's OWN sentence for this rule — "Spend over AED 500.00 needs an
   * approval". Preferred over composing one client-side: it is the same string
   * that lands on `approval.rule_summary` in the queue, so the WHEN column in
   * Settings and the "rule that caught it" column in the approver console say
   * the same words.
   */
  summary: z.string().nullable().optional(),
  /** Present when the rule was seeded from a policy threshold rather than added. */
  seeded_from: z.string().nullable().optional(),
  is_active: z.boolean(),
});
export type ApprovalRule = z.infer<typeof approvalRuleSchema>;

export const approvalRulesResponseSchema = z.object({ rules: z.array(approvalRuleSchema) });

export const approvalRuleCreateSchema = z.object({
  on_field: z.enum(RULE_FIELDS),
  value_minor: z.string().optional(),
  value_text: z.string().max(200).optional(),
  approvers: z.array(z.string().min(1).max(60)).min(1).max(2),
});
export type ApprovalRuleCreate = z.infer<typeof approvalRuleCreateSchema>;

/**
 * The THEN column's sentence: "Department head + Finance manager · both must
 * approve". Two approvers means DUAL approval, and saying so on the row is
 * what stops someone reading a two-name list as "either of these".
 */
export function approversSentence(approvers: readonly string[]): string {
  if (approvers.length === 0) return '—';
  if (approvers.length === 1) return approvers[0]!;
  return `${approvers.join(' + ')} · both must approve`;
}
